/**
 * The chat panel's two services: quevee's socket, and qv-data's saved
 * conversations (`docs/features/chat.md`).
 *
 * **The socket URL is derived from `VITE_QUEVEE_URL`**, the base every other
 * quevee call uses (`compile-expr.ts`, `analyst.ts`), so there is one setting
 * and not a second one to forget. `/` — the dev proxy — becomes this page's own
 * host, and `https://` becomes `wss://`: a hand-typed `ws://` against an HTTPS
 * page is refused as mixed content with an error naming neither.
 *
 * **History is qv-data's, read-only from here.** The browser lists, reads,
 * renames and deletes; it never appends. Messages are written by quevee, which
 * saw the turn — a message the browser could post is one the model never saw,
 * replayed to it as if it had (`services/qv-data/app/routers/chat.py`).
 *
 * Reads degrade and writes throw — `api/journal.ts`' split. A History list
 * that failed is a shorter list; a delete that failed silently is a
 * conversation that comes back.
 */

import { currentUserId, requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const QUEVEE_URL = (import.meta.env.VITE_QUEVEE_URL as string | undefined) ?? "";
/** qv-data — the workspace store's base, where the conversation tables are. */
const BASE = httpBase(import.meta.env.VITE_QV_SET_URL as string | undefined);

/** Whether this build has a quevee to talk to at all. */
export function chatConfigured(): boolean {
  return QUEVEE_URL !== "";
}

/**
 * Whether saved conversations can be listed — the History ▾ menu. The track
 * lists' test (`trackListsEnabled`): no workspace server, no rows to list. The
 * chat still works without it; nothing it says is kept where the panel can
 * find it again.
 */
export function historyConfigured(): boolean {
  return BASE !== "";
}

/** `wss://host/ws/chat` from the quevee base, `/` meaning this page's host. */
export function chatSocketUrl(
  base: string = QUEVEE_URL,
  page: Pick<Location, "protocol" | "host"> = window.location,
): string {
  const trimmed = base.replace(/\/$/, "");
  if (trimmed === "") {
    const scheme = page.protocol === "https:" ? "wss:" : "ws:";
    return `${scheme}//${page.host}/ws/chat`;
  }
  return `${trimmed.replace(/^http/, "ws")}/ws/chat`;
}

/** One conversation as History ▾ lists it. */
export interface ConversationSummary {
  id: string;
  /** The first message, cut to a line. */
  title: string;
  /** The vendor it is pinned to — never shown as a model, only kept. */
  provider: string;
  createdAt: string | null;
  updatedAt: string | null;
}

/** One stored message, exactly as the model saw it. */
export interface StoredMessage {
  role: "user" | "assistant";
  /** A string, or the Messages API's content blocks. */
  content: string | Array<Record<string, unknown>>;
  at: string | null;
  /** On the last answer of a turn: what the whole turn cost. */
  billedUsd?: number | null;
  inputTokens?: number;
  outputTokens?: number;
}

export interface Conversation extends ConversationSummary {
  messages: StoredMessage[];
}

export interface ConversationPage {
  conversations: ConversationSummary[];
  hasMore: boolean;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const separator = path.includes("?") ? "&" : "?";
  const url = `${BASE}${path}${separator}userId=${encodeURIComponent(currentUserId())}`;
  const auth = await requestHeaders();
  const response = await fetch(url, {
    ...init,
    headers: { "content-type": "application/json", ...auth, ...(init?.headers ?? {}) },
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail ?? `chat history ${response.status}`);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** My conversations, last spoken in first. A failure reads as none. */
export async function listConversations(limit = 30, offset = 0): Promise<ConversationPage> {
  try {
    return await call<ConversationPage>(`/chat/conversations?limit=${limit}&offset=${offset}`);
  } catch {
    return { conversations: [], hasMore: false };
  }
}

/** One conversation with every message. Throws — a resume must say why it failed. */
export const fetchConversation = (id: string): Promise<Conversation> =>
  call<Conversation>(`/chat/conversations/${encodeURIComponent(id)}`);

export const deleteConversation = (id: string): Promise<void> =>
  call<void>(`/chat/conversations/${encodeURIComponent(id)}`, { method: "DELETE" });

export const renameConversation = (id: string, title: string): Promise<ConversationSummary> =>
  call<ConversationSummary>(`/chat/conversations/${encodeURIComponent(id)}`, {
    method: "POST",
    body: JSON.stringify({ title }),
  });
